import { ContactModel } from "./ContactModels";
import { CustomerModel } from "./CustomerModels";
import { AttachmentModel } from "./EmailModels";

export type EmailContentModel = {
  subject: string;
  content: string;
  toneId: string;
};

export type ChaseResponseModel = {
  subject?: string;
  content: string;
  emailTo?: string[];
  emailCC?: string[];
  attachments?: AttachmentModel[];
};

export enum ActionTypeEnum {
  Chasable = "Chasable",
  NewInvoice = "NewInvoice",
  Payable = "Payable",
  Email = "Email",
  PromiseToPayBroken = "PromiseToPayBroken",
}

export const actionEnumToLabelMap = {
  [ActionTypeEnum.Chasable]: "reminders",
  [ActionTypeEnum.NewInvoice]: "invoices",
  [ActionTypeEnum.Payable]: "payables",
  [ActionTypeEnum.Email]: "emails",
  [ActionTypeEnum.PromiseToPayBroken]: "promise to pay broken",
};

export type ChasablesHubModel = {
  actions: ActionModel[];
  lastUpdated: string;
};

export type ActionModel = {
  id: string;
  contactId: string;
  externalId: string;
  type: ActionTypeEnum;
  assigedToId: string;
  groupId: string | null;
};

export type PersonalActionsModel = {
  mentions: MentionModel[];
  actions: ActionModel[];
};

export type MentionModel = {
  id: string;
  externalId: string;
  commentId: string;
  type: string;
  mentionedBy: {
    id?: string;
    fullName: string;
  };
  content: string;
  createdAt: string;
};

export type UserInfoModel = {
  id: string;
  fullName: string;
};

export type ChasableDetailsModel = {
  chasableId: string;
  chasableSource: string;
  hasMoreUnpaidInvoices: boolean;
  customerDetails: CustomerModel;
  invoiceDetails: SalesInvoiceDetailsModel;
  chasableHistory: ChasableHistoryModel[];
  assignedTo: UserInfoModel | null | undefined;
};

export enum InvoiceStatus {
  DRAFT = "DRAFT",
  SUBMITTED = "SUBMITTED",
  DELETED = "DELETED",
  AUTHORISED = "AUTHORISED",
  PAID = "PAID",
  VOIDED = "VOIDED",
}

export type InvoiceInfoModel = {
  id: string;
  amount: number;
  invoiceNumber: string;
  dueDate: string;
  plannedDate?: string | null;
  invoiceDate: string;
  status: InvoiceStatus;
  currency: string;
  link?: string;
  actionType?: string;
  groupId?: string;
  dispute?: DisputeModel;
  lastCompleted?: string;
};

export type InvoiceDetailsModel = InvoiceInfoModel & {
  remainingAmount: number;
  fullyPaidOn?: string;
};

export type SalesInvoiceDetailsModel = InvoiceDetailsModel & {
  productOrderNumber?: string;
};

export type ChasableHistoryModel = {
  date: string;
  detail: string;
};

export type CustomerChaseSummaryModel = ContactModel & {
  invoices: InvoiceInfoModel[];
};

export type ChaseInvoicesRequestModel = {
  customerId: string;
  invoiceIds: string[];
  rewriteEmail?: EmailContentModel;
  language?: string;
};

export type ChaseInvoicesResponseModel = {
  subject: string;
  content: string;
  emailTo: string[];
  emailCC?: string[];
  attachments: AttachmentModel[];
};

export type InvoiceSummaryResponse = {
  invoices: InvoiceDetailsModel[];
  link: string;
  totalAmount: number;
  totalDueAmount: number;
};

export type InvoiceTotalInfoModel = {
  totalAmount: number;
  totalDueAmount: number;
  link?: string;
};

/** API response types for GET /api/contacts/customers/chasables */

export type DisputeModel = {
  reasonCode: string;
  reason: string;
};

export type ChasableInvoiceApiModel = SalesInvoiceDetailsModel & {
  assignedToUserId?: string;
  lastCompleted?: string;
  dispute?: DisputeModel;
};

export type ChasableCustomerApiModel = {
  id: string;
  accountNumber: string;
  companyName: string;
  contactName: string;
  emails: string[];
  link: string;
  type: string;
  integration: string;
  businessTenantId: string;
  defaultCurrency: string;
  invoices: ChasableInvoiceApiModel[];
};

export type CustomersChasablesApiResponse = {
  chasables: ChasableCustomerApiModel[];
  lastUpdated: string;
};
